import { ChevronDown } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';

import type { PrazoCalculo } from '@/api/client';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { hojeIso, somarDias } from '@/pages/admin/calendarioForense/calendarioForense';
import { LancarFeriadoDialog } from './LancarFeriadoDialog';

interface ContaDoPrazoProps {
  calculo: PrazoCalculo;
  /** Pode lançar feriado ou suspensão na conta (fora de delegação em Leitura). */
  podeLancar?: boolean;
  /** O rótulo do botão que abre a conta (default "Ver a conta"). */
  rotulo?: string;
  /** O que mais fica guardado junto da conta (os demais prazos da lei): fechado,
   *  não tira espaço do texto da intimação. */
  children?: ReactNode;
}

/**
 * A conta ainda é refeita quando o calendário muda? O backend recalcula o que foi
 * disponibilizado nos últimos 120 dias ou ainda vence (vencimento.JANELA_DE_RECALCULO);
 * fora disso, lançar um dia não mudaria esta intimação.
 */
export function contaRecontavel(calculo: PrazoCalculo, hoje: string = hojeIso()): boolean {
  if (calculo.vencimento >= hoje) return true;
  const base = calculo.publicacao ?? calculo.inicio;
  return base >= somarDias(hoje, -119);
}

/**
 * A conta do vencimento, aberta a quem quiser conferir: os passos com os artigos.
 * Se faltou um dia, o advogado o lança dali (Bloco E): vale na hora, só nas contas dele.
 *
 * O interruptor do prazo em dobro (PUT /processos/{id}/prazo-em-dobro) saiu da
 * tela por decisão do Adriano (18/10/2026): aparecia em todo processo, inclusive
 * onde o advogado não representa ente público. O backend continua contando em
 * dobro o processo marcado; a forma de oferecer a marcação fica para depois.
 */
export function ContaDoPrazo({ calculo, podeLancar = false, rotulo = 'Ver a conta', children }: ContaDoPrazoProps) {
  const [aberta, setAberta] = useState(false);
  const [lancando, setLancando] = useState(false);
  const painelId = useId();

  return (
    <div className="mt-3 space-y-2">
      <Button
        type="button"
        variant="unstyled"
        aria-expanded={aberta}
        aria-controls={aberta ? painelId : undefined}
        onClick={() => setAberta((a) => !a)}
        className="flex h-auto min-h-[44px] w-full items-center justify-between gap-2 rounded-md p-0 text-left text-sm font-medium text-primary sm:min-h-0"
      >
        {rotulo}
        <ChevronDown className={cn('h-4 w-4 transition-transform', aberta && 'rotate-180')} />
      </Button>
      {aberta && (
        <div id={painelId} className="space-y-2">
          <ol className="list-decimal space-y-1 pl-5 text-xs leading-relaxed text-muted-foreground">
            {calculo.passos.map((passo, i) => (
              <li key={i} className="break-words">
                {passo}
              </li>
            ))}
          </ol>
          {podeLancar && contaRecontavel(calculo) && (
            <Button
              type="button"
              variant="link"
              className="h-auto min-h-[44px] p-0 text-sm sm:min-h-0"
              onClick={() => setLancando(true)}
            >
              Faltou um feriado ou uma suspensão?
            </Button>
          )}
          {children}
        </div>
      )}
      {lancando && <LancarFeriadoDialog calculo={calculo} onClose={() => setLancando(false)} />}
    </div>
  );
}
